/**
 * A faint preview of where the brush will actually land.
 *
 * The brush ring draws a flat disc, because that is what a brush is in two
 * dimensions. On a body it is a ball, and on a limb narrower than the brush it
 * reaches around onto that limb's other faces — which sit somewhere else
 * entirely in the template atlas. So one dab appears as separate patches, and
 * the only way to find that out was to paint and be surprised. Reported as a
 * bug; it never was one.
 *
 * This shows the answer before the stroke instead of after it. It uses
 * `landsHere`, the same rule the paint obeys, so the two cannot disagree —
 * a preview that lies teaches a wrong model of the tool and costs a design to
 * discover.
 *
 * ## What it costs
 *
 * `computeCoverage` is the expensive call in this app: about 1.2ms for a 120px
 * brush after the optimisations in shared/brush.ts. On every mouse-move that
 * would be 70ms a second for a preview nobody asked to be live. So it is
 * sampled by distance, on the same rule the stroke's own seam scan uses — a
 * brush that has not moved a sixth of its radius cannot have changed its
 * footprint enough to redraw for.
 */
import { computeCoverage, coverageStep } from "../../shared/brush";
import { CANVAS } from "../../shared/format";
import { partGroup, partSide } from "../../shared/parts";
import { partAt, positionAt } from "../../shared/position-map";
import { regionAt } from "../../shared/regions";
import type { Selection } from "../../shared/selection";
import { landsHere } from "./footprint";
import type { Surface } from "./paint";

const STYLE_ID = "footprint-overlay-style";

const CSS = `
/*
  Over the canvas, never in its way.

  pointer-events: none is not optional — this sits directly on the drawing
  surface, and a hint that swallowed the first millimetre of every stroke would
  be far worse than no hint. Below the symmetry guide's z-index, because that
  one is a line you aim with and this is a wash you glance at.
*/
.fp-overlay {
  position: absolute;
  pointer-events: none;
  z-index: 1;
  opacity: 0;
  transition: opacity var(--motion-fast) var(--motion-ease);
}
.fp-overlay[data-on] { opacity: 1; }
`;

export interface FootprintOverlayOptions {
  canvas: HTMLCanvasElement;
  surface: () => Surface | null;
  /** Brush diameter in template pixels. */
  size: () => number;
  crossParts: () => boolean;
  selection: () => Selection;
  doc?: Document;
}

export interface FootprintOverlay {
  /** Preview the footprint at a template-space point. */
  show(x: number, y: number): void;
  hide(): void;
  /** Re-measure against the canvas after a resize. */
  place(): void;
  destroy(): void;
}

export function mountFootprintOverlay(opts: FootprintOverlayOptions): FootprintOverlay {
  const doc = opts.doc ?? document;
  const stage = opts.canvas.parentElement;
  if (!stage) throw new Error("footprint overlay: the canvas has no stage to sit in");

  if (!doc.getElementById(STYLE_ID)) {
    const style = doc.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    doc.head.appendChild(style);
  }
  // Same guard, same reason, as the symmetry overlay: the offsets below are
  // relative to the nearest positioned ancestor.
  if (getComputedStyle(stage).position === "static") stage.style.position = "relative";

  const canvas = doc.createElement("canvas");
  canvas.className = "fp-overlay";
  canvas.setAttribute("aria-hidden", "true");
  canvas.width = CANVAS.width;
  canvas.height = CANVAS.height;
  stage.appendChild(canvas);
  const ctx = canvas.getContext("2d");

  /** Where the last preview was computed, so a small move costs nothing. */
  let lastAt: { x: number; y: number } | null = null;
  /** What the last preview dirtied, so clearing stays proportional to it. */
  let dirty: { x: number; y: number; w: number; h: number } | null = null;

  function place() {
    canvas.style.left = `${opts.canvas.offsetLeft}px`;
    canvas.style.top = `${opts.canvas.offsetTop}px`;
    canvas.style.width = `${opts.canvas.offsetWidth}px`;
    canvas.style.height = `${opts.canvas.offsetHeight}px`;
  }

  function clear() {
    if (ctx && dirty) ctx.clearRect(dirty.x, dirty.y, dirty.w, dirty.h);
    dirty = null;
  }

  function show(x: number, y: number) {
    if (!ctx) return;
    const surface = opts.surface();
    if (!surface) return;

    const radius = opts.size() / 2;
    const step = coverageStep(radius);
    if (lastAt) {
      const dx = x - lastAt.x;
      const dy = y - lastAt.y;
      // Not moved enough for the footprint to have changed meaningfully.
      if (dx * dx + dy * dy < step * step && canvas.dataset.on !== undefined) return;
    }

    const at = positionAt(surface.map, Math.round(x), Math.round(y));
    if (!at) {
      hide();
      return;
    }
    lastAt = { x, y };

    const ownPart = partAt(surface.map, Math.round(x), Math.round(y));
    const rules = {
      map: surface.map,
      ownRegion: regionAt(Math.round(x), Math.round(y)),
      ownGroup: ownPart ? partGroup(ownPart) : null,
      // Both arms share a group, so the group alone lets a stroke cross to the
      // opposite limb. The preview has to know it too, or it promises a
      // footprint the paint will not honour.
      ownSide: ownPart ? partSide(ownPart) : null,
      crossParts: opts.crossParts(),
      selection: opts.selection(),
    };

    /*
      Only the parts that carry across a fold are drawn.

      Under the pointer there is already a brush ring saying exactly this, and
      washing the area it encircles would fight it. What is worth showing is
      the half of the footprint nobody expects.
    */
    const marks: { x: number; y: number }[] = [];
    // Annotated, because CANVAS.width is a literal type and would otherwise
    // infer these as 585 and 559 rather than as numbers.
    let x0: number = CANVAS.width;
    let y0: number = CANVAS.height;
    let x1 = 0;
    let y1 = 0;
    for (const t of computeCoverage(surface.map, surface.bounds, at, radius, surface.unit)) {
      if (!landsHere(t, rules)) continue;
      marks.push({ x: t.x, y: t.y });
      if (t.x < x0) x0 = t.x;
      if (t.y < y0) y0 = t.y;
      if (t.x > x1) x1 = t.x;
      if (t.y > y1) y1 = t.y;
    }

    clear();
    if (marks.length === 0) {
      delete canvas.dataset.on;
      return;
    }

    /*
      One putImageData rather than a fillRect per texel.

      A 120px brush can reach fourteen thousand texels, and fourteen thousand
      one-pixel fills is a frame's worth of work for a hint. The buffer covers
      only the bounding box of what was found.
    */
    const w = x1 - x0 + 1;
    const h = y1 - y0 + 1;
    const img = ctx.createImageData(w, h);
    const px = img.data;
    for (const m of marks) {
      const i = ((m.y - y0) * w + (m.x - x0)) * 4;
      // The accent, faint. Bright enough to find, dim enough to draw over.
      px[i] = 0x2f;
      px[i + 1] = 0x8a;
      px[i + 2] = 0xff;
      px[i + 3] = 90;
    }
    ctx.putImageData(img, x0, y0);
    dirty = { x: x0, y: y0, w, h };
    canvas.dataset.on = "";
  }

  function hide() {
    clear();
    lastAt = null;
    delete canvas.dataset.on;
  }

  const onResize = () => place();
  const observer = new ResizeObserver(() => place());
  observer.observe(opts.canvas);
  window.addEventListener("resize", onResize);
  place();

  return {
    show,
    hide,
    place,
    destroy() {
      observer.disconnect();
      window.removeEventListener("resize", onResize);
      canvas.remove();
    },
  };
}
